import React, { useState, useEffect } from "react";
import { FaArrowLeft, FaDownload, FaPrint, FaHistory, FaCalculator, FaYoutube, FaInstagram, FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getAffiliateEarnings, calculateAffiliatePayments, confirmPaymentReceipt } from "../services/affiliateService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { printComponent } from "../../utils/printUtils";
import { exportToCSV } from "../../utils/exportUtils";
import PrintableEarnings from "./PrintableEarnings";
import { useSelector } from 'react-redux';
import LoadingSpinner from "../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../constants";

const Earnings = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEarnings, setTotalEarnings] = useState({
    totalDue: 0,
    amountPaid: 0,
    amountUnpaid: 0
  });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [autoCalculating, setAutoCalculating] = useState(false);
  const [hasAutoCalculated, setHasAutoCalculated] = useState(false);
  const [calculationData, setCalculationData] = useState(null);
  const [detailedCalculations, setDetailedCalculations] = useState([]);
  const [lastCalculationTime, setLastCalculationTime] = useState(null);
  const [calculationCache, setCalculationCache] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        // Try to get userId from multiple sources
        let userId = localStorage.getItem('userId');

        // If not in localStorage, try to get from Redux state
        if (!userId && user?._id) {
          userId = user._id;
          localStorage.setItem('userId', userId);
        }

        // If still no userId, try to get from userData in localStorage
        if (!userId) {
          const userData = localStorage.getItem('userData');
          if (userData) {
            const parsedUserData = JSON.parse(userData);
            userId = parsedUserData._id;
            if (userId) {
              localStorage.setItem('userId', userId);
            }
          }
        }

        if (!userId) {
          toast.error('User ID not found. Please log in again.');
          navigate(`${INFLUENCER_BASE}/login`);
          return;
        }

        // Validate userId format
        if (!/^[0-9a-fA-F]{24}$/.test(userId)) {
          toast.error('Invalid user ID format. Please log in again.');
          navigate(`${INFLUENCER_BASE}/login`);
          return;
        }

        // console.log('Fetching earnings for user:', userId);
        const response = await getAffiliateEarnings(userId);

        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch earnings');
        }

        setEarnings(response.data);

        // Extract unique campaigns from earnings data
        const campaigns = response.data
          .filter(earning => earning.brandName && earning.campaignName)
          .map(earning => ({
            id: `${earning.brandName}-${earning.campaignName}`,
            brandName: earning.brandName,
            campaignName: earning.campaignName,
            displayName: `${earning.brandName} - ${earning.campaignName}`
          }))
          .filter((campaign, index, self) => 
            index === self.findIndex(c => c.id === campaign.id)
          )
          .sort((a, b) => a.displayName.localeCompare(b.displayName));

        setAvailableCampaigns(campaigns);

        // Extract and flatten payment history from all earnings
        const allPaymentHistory = response.data.reduce((acc, earning) => {
          // Skip earnings with missing data
          if (!earning.brandName || !earning.campaignName) {
            console.warn('Skipping earnings record with missing data:', earning);
            return acc;
          }

          const history = earning.paymentHistory?.map(payment => ({
            ...payment,
            brandName: earning.brandName,
            campaignName: earning.campaignName
          })) || [];
          return [...acc, ...history];
        }, []);

        // Sort payment history by date (most recent first)
        const sortedHistory = allPaymentHistory.sort((a, b) =>
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );

        setPaymentHistory(sortedHistory);

        // Calculate totals from the current earnings (only include valid records)
        const totals = response.data
          .filter(earning => earning.brandName && earning.campaignName) // Filter out invalid records
          .reduce((acc, curr) => ({
            totalDue: acc.totalDue + (curr.totalDue || 0),
            amountPaid: acc.amountPaid + (curr.amountPaid || 0),
            amountUnpaid: acc.amountUnpaid + (curr.amountUnpaid || 0)
          }), { totalDue: 0, amountPaid: 0, amountUnpaid: 0 });

        setTotalEarnings(totals);

        // Show warning if there are invalid records
        const invalidRecords = response.data.filter(earning => !earning.brandName || !earning.campaignName);
        if (invalidRecords.length > 0) {
          console.warn(`Found ${invalidRecords.length} earnings records with missing campaign or brand data`);
          toast.warning(`Some earnings records have missing data and were filtered out. Please contact support if this persists.`);
        }
      } catch (error) {
        console.error('Error fetching earnings:', error);
        toast.error(error.message || 'Failed to fetch earnings');
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [user, navigate]);

  // Check if we need to recalculate based on cache and time
  const shouldRecalculate = () => {
    // If no cache exists, we need to calculate
    if (!calculationCache) return true;
    
    // If no last calculation time, we need to calculate
    if (!lastCalculationTime) return true;
    
    // If it's been more than 5 minutes since last calculation, we can recalculate
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (new Date(lastCalculationTime) < fiveMinutesAgo) return true;
    
    // Otherwise, use cached data
    return false;
  };

  // Auto-calculate payments after initial data is loaded (only if needed)
  useEffect(() => {
    const autoCalculatePayments = async () => {
      if (!loading && !hasAutoCalculated && user?._id && shouldRecalculate()) {
        setAutoCalculating(true);
        try {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Small delay for better UX
          await performCalculation(true); // true indicates auto calculation
          setHasAutoCalculated(true);
        } catch (error) {
          console.error('Auto-calculation failed:', error);
          // Don't show error toast for auto-calculation, just log it
        } finally {
          setAutoCalculating(false);
        }
      } else if (calculationCache && !hasAutoCalculated) {
        // Use cached calculation data
        setCalculationData(calculationCache);
        setHasAutoCalculated(true);
        console.log('Using cached calculation data');
      }
    };

    autoCalculatePayments();
  }, [loading, hasAutoCalculated, user, calculationCache, lastCalculationTime]);

  const performCalculation = async (isAutoCalculation = false) => {
    try {
      // Try to get userId from multiple sources
      let userId = localStorage.getItem('userId');

      if (!userId && user?._id) {
        userId = user._id;
        localStorage.setItem('userId', userId);
      }

      if (!userId) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          userId = parsedUserData._id;
          if (userId) {
            localStorage.setItem('userId', userId);
          }
        }
      }

      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await calculateAffiliatePayments(userId);

      if (response.success) {
        // Cache the calculation data
        setCalculationCache(response.data);
        setLastCalculationTime(new Date().toISOString());
        setCalculationData(response.data);

        // Set detailed calculations if available
        if (response.data.detailedCalculations) {
          setDetailedCalculations(response.data.detailedCalculations);
        }

        // Update current earnings with calculated data
        if (response.data.earnings && response.data.earnings.length > 0) {
          setEarnings(response.data.earnings);

          // Recalculate totals
          const totals = response.data.earnings
            .filter(earning => earning.brandName && earning.campaignName)
            .reduce((acc, curr) => ({
              totalDue: acc.totalDue + (curr.totalDue || 0),
              amountPaid: acc.amountPaid + (curr.amountPaid || 0),
              amountUnpaid: acc.amountUnpaid + (curr.amountUnpaid || 0)
            }), { totalDue: 0, amountPaid: 0, amountUnpaid: 0 });

          setTotalEarnings(totals);

          if (!isAutoCalculation) {
            if (totals.totalDue > 0) {
              const unpaidAmount = totals.totalDue - totals.amountPaid;
              if (totals.amountPaid > 0) {
                toast.success(`Earnings updated! Total earned: ₹${totals.totalDue.toFixed(2)}, Already paid: ₹${totals.amountPaid.toFixed(2)}, Remaining: ₹${unpaidAmount.toFixed(2)}`);
              } else {
                toast.success(`Earnings calculated! Total due: ₹${totals.totalDue.toFixed(2)} (based on current YouTube + Instagram performance)`);
              }
            } else {
              toast.info('No earnings found. Submit your posts/videos and get them approved by brands first.');
            }
          }
        } else if (!isAutoCalculation) {
          toast.info('No payment data calculated. Check your campaign participations and content approvals.');
        }
      } else {
        if (!isAutoCalculation) {
          toast.error(response.message || 'Failed to calculate payments');
        }
        throw new Error(response.message || 'Calculation failed');
      }
    } catch (error) {
      console.error('Error calculating payments:', error);
      if (!isAutoCalculation) {
        if (error.message?.includes('No approved media')) {
          toast.warning('No approved content found. Submit your posts/videos and get them approved by brands first.');
        } else if (error.message?.includes('No campaigns found')) {
          toast.info('You are not participating in any active campaigns yet.');
        } else {
          toast.error('Failed to calculate payments. Please try again.');
        }
      }
      throw error;
    }
  };

  const calculatePayments = async () => {
    setCalculating(true);
    try {
      await performCalculation(false);
    } finally {
      setCalculating(false);
    }
  };

  // Handle payment confirmation
  const handlePaymentConfirmation = async (paymentId, historyId, isReceived) => {
    try {
      const response = await confirmPaymentReceipt(paymentId, historyId, isReceived);
      
      if (response.success) {
        // Update the local state
        setPaymentHistory(prev => prev.map(payment => {
          if (payment.paymentId === paymentId && payment.historyId === historyId) {
            return {
              ...payment,
              isReceived: response.data.isReceived,
              receivedAt: response.data.receivedAt
            };
          }
          return payment;
        }));

        toast.success(isReceived ? 'Payment confirmed as received!' : 'Marked as not received yet. You can confirm when payment arrives.');
      } else {
        toast.error(response.message || 'Failed to update payment confirmation');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      const errorMessage = error.message || error.error?.message || 'Failed to update payment confirmation. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleExport = () => {
    // Use filtered data for export
    const validEarnings = filteredEarnings;
    const validPaymentHistory = filteredPaymentHistory;

    // Headers for earnings summary
    const earningsHeaders = [
      { label: 'Brand', accessor: row => row.brandName || 'Unknown Brand' },
      { label: 'Campaign', accessor: row => row.campaignName || 'Unknown Campaign' },
      { label: 'Total Due', accessor: row => row.totalDue || 0, isCurrency: true },
      { label: 'Amount Paid', accessor: row => row.amountPaid || 0, isCurrency: true },
      { label: 'Amount Unpaid', accessor: row => row.amountUnpaid || 0, isCurrency: true },
      { label: 'Last Updated', accessor: row => row.lastUpdated, isDate: true }
    ];

    // Headers for payment history
    const historyHeaders = [
      { label: 'Date', accessor: row => row.updatedAt, isDate: true },
      { label: 'Brand', accessor: row => row.brandName || 'Unknown Brand' },
      { label: 'Campaign', accessor: row => row.campaignName || 'Unknown Campaign' },
      { label: 'Total Due', accessor: row => row.totalDue || 0, isCurrency: true },
      { label: 'Amount Paid', accessor: row => row.amountPaid || 0, isCurrency: true },
      { label: 'Amount Unpaid', accessor: row => row.amountUnpaid || 0, isCurrency: true },
      { label: 'Updated By', accessor: row => row.updatedBy?.name || row.brandName || 'System' }
    ];

    // Export earnings summary
    exportToCSV(validEarnings, 'earnings_summary.csv', earningsHeaders);

    // Export payment history if available
    if (validPaymentHistory.length > 0) {
      exportToCSV(validPaymentHistory, 'payment_history.csv', historyHeaders);
      toast.success('Exported earnings summary and payment history');
    } else {
      toast.success('Exported earnings summary');
    }
  };

  const handlePrint = () => {
    const printProps = {
      earnings: filteredEarnings,
      totalEarnings: filteredTotals,
      paymentHistory: filteredPaymentHistory,
      userDetails: user,
      selectedCampaign: selectedCampaign,
      availableCampaigns: availableCampaigns
    };
    printComponent(PrintableEarnings, printProps);
  };

  // Filter earnings based on selected campaign
  const getFilteredEarnings = () => {
    if (selectedCampaign === 'all') {
      return earnings.filter(earning => earning.brandName && earning.campaignName);
    }
    return earnings.filter(earning => 
      earning.brandName && earning.campaignName && 
      `${earning.brandName}-${earning.campaignName}` === selectedCampaign
    );
  };

  // Filter payment history based on selected campaign
  const getFilteredPaymentHistory = () => {
    if (selectedCampaign === 'all') {
      return paymentHistory.filter(payment => payment.brandName && payment.campaignName);
    }
    return paymentHistory.filter(payment => 
      payment.brandName && payment.campaignName && 
      `${payment.brandName}-${payment.campaignName}` === selectedCampaign
    );
  };

  // Calculate totals for filtered earnings
  const getFilteredTotals = () => {
    const filteredEarnings = getFilteredEarnings();
    return filteredEarnings.reduce((acc, curr) => ({
      totalDue: acc.totalDue + (curr.totalDue || 0),
      amountPaid: acc.amountPaid + (curr.amountPaid || 0),
      amountUnpaid: acc.amountUnpaid + (curr.amountUnpaid || 0)
    }), { totalDue: 0, amountPaid: 0, amountUnpaid: 0 });
  };

  const filteredEarnings = getFilteredEarnings();
  const filteredPaymentHistory = getFilteredPaymentHistory();
  const filteredTotals = getFilteredTotals();

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading your earnings..." />
      </div>
    );
  }

  if (autoCalculating) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Calculating your earnings..." size="xlarge" />
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
        <ToastContainer position="top-right" autoClose={2000} />

        <button
          type="button"
          onClick={() => navigate(`${INFLUENCER_BASE}/dashboard`)}
          className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
        >
          <FaArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>

        {/* Header Section */}
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">My Earnings</h1>
              <p className="mt-1 text-sm text-gray-500">Track affiliate earnings, payments, and campaign revenue.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              {availableCampaigns.length > 0 && (
                <select
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:w-auto sm:min-w-[220px]"
                  aria-label="Filter by Campaign"
                >
                  <option value="all">All Campaigns ({availableCampaigns.length})</option>
                  {availableCampaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.displayName}
                    </option>
                  ))}
                </select>
              )}

              <button
                type="button"
                onClick={calculatePayments}
                disabled={calculating}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50 sm:text-sm"
              >
                <FaCalculator className="h-3.5 w-3.5" />
                {calculating ? 'Recalculating...' : 'Re-calculate Earnings'}
              </button>

              <button
                type="button"
                onClick={handleExport}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
              >
                <FaDownload className="h-3.5 w-3.5" /> Export
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:text-sm"
              >
                <FaPrint className="h-3.5 w-3.5" /> Print
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:mb-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-2 flex items-start justify-between gap-2">
              <h3 className="text-sm font-medium text-gray-500">Total Earnings</h3>
              {calculationData && (
                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-600 sm:text-xs">
                  Calculated
                </span>
              )}
            </div>
            <p className="text-base font-bold text-gray-900 md:text-lg">
              {filteredTotals.totalDue.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <h3 className="mb-2 text-sm font-medium text-gray-500">Total Paid Amount</h3>
            <p className="text-base font-bold text-green-600 md:text-lg">
              {filteredTotals.amountPaid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:col-span-2 sm:p-5 lg:col-span-1">
            <h3 className="mb-2 text-sm font-medium text-gray-500">Total Pending Amount</h3>
            <p className="text-base font-bold text-red-600 md:text-lg">
              {filteredTotals.amountUnpaid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </p>
          </div>
        </div>

        {/* Earnings Table */}
        <div className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:mb-6">
          <div className="border-b border-gray-100 px-4 py-3 sm:px-5 sm:py-4">
            <h3 className="text-sm font-semibold text-gray-900 md:text-base">Current Earnings by Campaign</h3>
          </div>

          {/* Mobile Card View - Scrollable */}
          <div className="block sm:hidden max-h-96 overflow-y-auto relative">
            {/* Scroll indicator for mobile */}
            {filteredEarnings.length > 3 && (
              <div className="absolute top-2 right-2 z-10 bg-gray-800 text-white text-xs px-2 py-1 rounded-full opacity-75">
                Scroll to see more
              </div>
            )}
            {filteredEarnings.map((earning, index) => (
                <div key={index} className="p-4 border-b border-gray-100 last:border-b-0">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-base">{earning.brandName || 'Unknown Brand'}</h4>
                      <p className="text-sm text-gray-600">{earning.campaignName || 'Unknown Campaign'}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
                        <p className="font-semibold text-gray-900 text-sm">
                          {(earning.totalDue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Paid Amount</p>
                        <p className="font-semibold text-green-600 text-sm">
                          {(earning.amountPaid || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Pending Amount</p>
                        <p className="font-semibold text-red-600 text-sm">
                          {(earning.amountUnpaid || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Last Updated: {earning.lastUpdated ? new Date(earning.lastUpdated).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            {filteredEarnings.length === 0 && (
              <div className="px-4 py-10 text-center text-gray-500">
                <p className="mb-1 text-base font-semibold text-gray-900">
                  {selectedCampaign === 'all' ? 'No earnings found' : 'No earnings for selected campaign'}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedCampaign === 'all' 
                    ? "You don't have any earnings records yet." 
                    : 'Try selecting a different campaign or check back later.'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto max-h-96 overflow-y-auto relative">
            {/* Scroll indicator for desktop */}
            {filteredEarnings.length > 5 && (
              <div className="absolute top-2 right-2 z-10 bg-gray-800 text-white text-xs px-2 py-1 rounded-full opacity-75">
                Scroll to see more
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Earnings
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEarnings.map((earning, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {earning.brandName || 'Unknown Brand'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {earning.campaignName || 'Unknown Campaign'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(earning.totalDue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {(earning.amountPaid || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-red-600">
                        {(earning.amountUnpaid || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {earning.lastUpdated ? new Date(earning.lastUpdated).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                {filteredEarnings.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center text-gray-500">
                      <p className="mb-1 text-base font-semibold text-gray-900">
                        {selectedCampaign === 'all' ? 'No earnings found' : 'No earnings for selected campaign'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedCampaign === 'all' 
                          ? "You don't have any earnings records yet." 
                          : 'Try selecting a different campaign or check back later.'
                        }
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calculation Details Section */}
        {detailedCalculations.length > 0 && (
          <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
            <div className="mb-4 flex flex-col gap-3 sm:mb-5 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-gray-900 md:text-base">Revenue Calculation Details</h2>
              <button
                type="button"
                onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                className="text-sm font-medium text-orange-600 transition hover:text-orange-700"
              >
                {showCalculationDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {showCalculationDetails && (
              <div className="space-y-6">
                {detailedCalculations.map((calculation, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-2 md:p-4">
                    <div className="mb-4">
                      <h3 className="text-sm md:text-lg font-semibold text-gray-800 mb-2">
                        {calculation.campaign.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-[10px] md:text-sm">
                          {calculation.campaign.type}
                        </span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-[10px] md:text-sm">
                          {calculation.campaign.commission.model}
                        </span>
                      </div>
                    </div>

                    {/* Revenue Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="text-[10px] md:text-sm font-semibold text-red-800 mb-2 flex items-center">
                          <FaYoutube className="mr-2" />
                          YouTube Revenue
                        </h4>
                        <p className="text-[10px] md:text-sm font-bold text-red-600">
                          ₹{calculation.youtube.revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {calculation.youtube.approvedVideos}/{calculation.youtube.totalVideos} videos
                        </p>
                      </div>

                      <div className="bg-pink-50 p-4 rounded-lg">
                        <h4 className="text-[10px] md:text-sm font-semibold text-pink-800 mb-2 flex items-center">
                          <FaInstagram className="mr-2" />
                          Instagram Revenue
                        </h4>
                        <p className="text-[10px] md:text-sm font-bold text-pink-600">
                          ₹{calculation.instagram.revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-pink-600 mt-1">
                          {calculation.instagram.approvedPosts}/{calculation.instagram.totalPosts} posts
                        </p>
                      </div>

                      <div className="bg-orange-50 p-4 rounded-lg sm:col-span-2 lg:col-span-1">
                        <h4 className="text-[10px] md:text-sm font-semibold text-orange-800 mb-2">Total Revenue</h4>
                        <p className="text-[10px] md:text-sm font-bold text-orange-600">
                          ₹{calculation.total.revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          {calculation.campaign.commission.currency?.symbol || '₹'}
                        </p>
                      </div>
                    </div>

                    {/* Commission Details */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="text-[10px] md:text-sm font-semibold text-gray-800 mb-2">Commission Structure</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-600">CPE (Cost Per Engagement):</span>
                          <span className="ml-2 font-medium">
                            ₹{calculation.campaign.commission.cpe || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">CPM (Cost Per Mille):</span>
                          <span className="ml-2 font-medium">
                            ₹{calculation.campaign.commission.cpm || 0}
                          </span>
                        </div>
                        {calculation.campaign.commission.amount && (
                          <div>
                            <span className="text-gray-600">Fixed Amount:</span>
                            <span className="ml-2 font-medium">
                              ₹{calculation.campaign.commission.amount}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* YouTube Media Breakdown */}
                    {calculation.youtube.media.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-[10px] md:text-sm font-semibold text-gray-800 mb-3 flex items-center">
                          <FaYoutube className="mr-2 text-red-600" />
                          YouTube Media Breakdown
                        </h4>
                        {/* Mobile (no horizontal scroll) */}
                        <div className="space-y-2 sm:hidden">
                          {calculation.youtube.media.map((video, videoIndex) => (
                            <div key={videoIndex} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs font-semibold text-gray-900 break-words">
                                {video.title}
                              </p>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                                <div className="bg-gray-50 rounded-md px-2 py-1">
                                  <span className="text-gray-500">Engagements</span>
                                  <div className="font-semibold text-gray-900">{video.engagements.toLocaleString()}</div>
                                </div>
                                <div className="bg-gray-50 rounded-md px-2 py-1">
                                  <span className="text-gray-500">Views</span>
                                  <div className="font-semibold text-gray-900">{video.reachs.toLocaleString()}</div>
                                </div>
                                <div className="bg-gray-50 rounded-md px-2 py-1 col-span-2">
                                  <span className="text-gray-500">Revenue</span>
                                  <div className="font-semibold text-gray-900">₹{video.revenue.toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop/tablet */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Video Title</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Engagements</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {calculation.youtube.media.map((video, videoIndex) => (
                                <tr key={videoIndex} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-xs text-gray-900 truncate max-w-xs" title={video.title}>
                                    {video.title}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900">
                                    {video.engagements.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900">
                                    {video.reachs.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                    ₹{video.revenue.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Instagram Media Breakdown */}
                    {calculation.instagram.media.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-[10px] md:text-sm font-semibold text-gray-800 mb-3 flex items-center">
                          <FaInstagram className="mr-2 text-pink-600" />
                          Instagram Media Breakdown
                        </h4>
                        {/* Mobile (no horizontal scroll) */}
                        <div className="space-y-2 sm:hidden">
                          {calculation.instagram.media.map((post, postIndex) => (
                            <div key={postIndex} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs font-semibold text-gray-900 break-words">
                                {post.caption}
                              </p>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                                <div className="bg-gray-50 rounded-md px-2 py-1">
                                  <span className="text-gray-500">Engagements</span>
                                  <div className="font-semibold text-gray-900">{post.engagements.toLocaleString()}</div>
                                </div>
                                <div className="bg-gray-50 rounded-md px-2 py-1">
                                  <span className="text-gray-500">Reach</span>
                                  <div className="font-semibold text-gray-900">{post.reachs.toLocaleString()}</div>
                                </div>
                                <div className="bg-gray-50 rounded-md px-2 py-1 col-span-2">
                                  <span className="text-gray-500">Revenue</span>
                                  <div className="font-semibold text-gray-900">₹{post.revenue.toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop/tablet */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Post Caption</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Engagements</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reachs</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {calculation.instagram.media.map((post, postIndex) => (
                                <tr key={postIndex} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-xs text-gray-900 truncate max-w-xs" title={post.caption}>
                                    {post.caption}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900">
                                    {post.engagements.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900">
                                    {post.reachs.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                    ₹{post.revenue.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* No Media Message */}
                    {calculation.youtube.media.length === 0 && calculation.instagram.media.length === 0 && (
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="text-[10px] md:text-sm font-semibold text-yellow-800 mb-2">No Approved Media Found</h4>
                        <p className="text-xs text-yellow-700">
                          No approved YouTube videos or Instagram posts were found for this campaign. 
                          Submit your content and get it approved by the brand to start earning.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment History Section */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex flex-col gap-2 sm:mb-5 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-semibold text-gray-900 md:text-base">Payment History</h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FaHistory />
              <span>Transaction History</span>
            </div>
          </div>

          {filteredPaymentHistory.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <p className="mb-1 text-base font-semibold text-gray-900">
                {selectedCampaign === 'all' ? 'No payment history yet' : 'No payment history for selected campaign'}
              </p>
              <p className="text-sm text-gray-500">
                {selectedCampaign === 'all' 
                  ? 'Your payment history will appear here once brands make payments.' 
                  : 'Try selecting a different campaign or check back later.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto relative">
              {/* Scroll indicator for payment history */}
              {filteredPaymentHistory.length > 3 && (
                <div className="absolute top-2 right-2 z-10 bg-gray-800 text-white text-xs px-2 py-1 rounded-full opacity-75">
                  Scroll to see more
                </div>
              )}
              {filteredPaymentHistory.map((payment, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-2">
                          <span className="font-semibold text-gray-900 text-base">
                            {payment.brandName || 'Unknown Brand'}
                          </span>
                          <span className="hidden sm:inline text-sm text-gray-500">•</span>
                          <span className="text-sm text-gray-700">
                            {payment.campaignName || 'Unknown Campaign'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {payment.updatedAt ? new Date(payment.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Updated by: {payment.updatedBy?.name || payment.brandName || 'System'}
                        </div>
                        {payment.referenceMessage && (
                          <div className="text-xs sm:text-sm text-gray-700">
                            <span className="font-semibold text-orange-600">Reference: </span>
                            <span className="text-orange-500">{payment.referenceMessage}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-left sm:text-right">
                        <div className="text-lg font-semibold text-green-600">
                          ₹{(payment.amountPaid || 0).toFixed(2)} paid
                        </div>
                        <div className="text-sm text-gray-500">
                          of ₹{(payment.totalDue || 0).toFixed(2)} due
                        </div>
                        {payment.amountUnpaid > 0 && (
                          <div className="text-xs text-orange-600 mt-1">
                            ₹{payment.amountUnpaid.toFixed(2)} pending
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Revenue Breakdown (if available) */}
                    {payment.revenueBreakdown && (
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 mt-3">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Revenue Breakdown</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          {payment.revenueBreakdown.youtubeRevenue > 0 && (
                            <div className="bg-white rounded-lg p-2">
                              <div className="text-gray-500">YouTube</div>
                              <div className="font-semibold text-red-600">
                                ₹{payment.revenueBreakdown.youtubeRevenue.toFixed(2)}
                              </div>
                            </div>
                          )}
                          {payment.revenueBreakdown.instagramRevenue > 0 && (
                            <div className="bg-white rounded-lg p-2">
                              <div className="text-gray-500">Instagram</div>
                              <div className="font-semibold text-pink-600">
                                ₹{payment.revenueBreakdown.instagramRevenue.toFixed(2)}
                              </div>
                            </div>
                          )}
                          <div className="bg-white rounded-lg p-2">
                            <div className="text-gray-500">Total</div>
                            <div className="font-semibold text-orange-600">
                              ₹{(payment.revenueBreakdown.totalRevenue || 0).toFixed(2)}
                            </div>
                          </div>
                          {payment.revenueBreakdown.incrementalRevenue && (
                            <div className="bg-white rounded-lg p-2">
                              <div className="text-gray-500">Incremental</div>
                              <div className="font-semibold text-green-600">
                                ₹{payment.revenueBreakdown.incrementalRevenue.toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment Status and Confirmation */}
                    <div className="mt-3 flex flex-col space-y-3">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${payment.amountUnpaid === 0
                              ? 'bg-green-100 text-green-800'
                              : payment.amountPaid > 0
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {payment.amountUnpaid === 0 ? 'Fully Paid' : payment.amountPaid > 0 ? 'Partial Payment' : 'Unpaid'}
                        </span>

                        {/* Statistics Summary (if available) */}
                        {payment.incrementalStats && (
                          <div className="text-xs text-gray-500">
                            <span className="mr-2">📊 Performance included:</span>
                            {payment.incrementalStats.youtube && (
                              <span className="mr-2">
                                YT: +{payment.incrementalStats.youtube.totalViews?.toLocaleString() || 0} views
                              </span>
                            )}
                            {payment.incrementalStats.instagram && (
                              <span>
                                IG: +{payment.incrementalStats.instagram.totalEngagements?.toLocaleString() || 0} engagements
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Payment Confirmation Section */}
                      {payment.amountPaid > 0 && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Payment Received?</span>
                              {payment.isReceived === true && payment.receivedAt ? (
                                <span className="flex items-center text-sm font-semibold text-green-600">
                                  <FaCheck className="mr-1" />
                                  Yes
                                </span>
                              ) : payment.isReceived === false ? (
                                <span className="flex items-center text-sm font-semibold text-orange-600">
                                  <FaTimes className="mr-1" />
                                  Not Yet
                                </span>
                              ) : (
                                <span className="text-sm text-gray-500">
                                  Pending
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handlePaymentConfirmation(payment.paymentId, payment.historyId, true)}
                                disabled={payment.isReceived === true && payment.receivedAt}
                                className={`rounded-xl px-3 py-1.5 text-sm font-medium transition ${
                                  payment.isReceived === true && payment.receivedAt
                                    ? 'cursor-not-allowed bg-gray-200 text-gray-500 opacity-50'
                                    : 'bg-green-500 text-white hover:bg-green-600'
                                }`}
                              >
                                <FaCheck className="mr-1 inline" />
                                Yes
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePaymentConfirmation(payment.paymentId, payment.historyId, false)}
                                disabled={payment.isReceived === true && payment.receivedAt}
                                className={`rounded-xl px-3 py-1.5 text-sm font-medium transition ${
                                  payment.isReceived === true && payment.receivedAt
                                    ? 'cursor-not-allowed bg-gray-200 text-gray-500 opacity-50'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                              >
                                <FaTimes className="mr-1 inline" />
                                No
                              </button>
                            </div>
                          </div>
                          {payment.isReceived === true && payment.receivedAt && (
                            <div className="mt-2 text-xs text-gray-500">
                              Confirmed on: {new Date(payment.receivedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </div>
    </div>
  );
};

export default Earnings;
